import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Dropdown, DropdownWithProperties } from '../shared/models/dropdown';
import {
  Criteria,
  GenerateReportResult,
  NextDateResult,
  ReportSettingsSaveDto,
  ReportUserDto,
} from '../interfaces/reportSettingsSaveDto';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { HttpParams } from '@angular/common/http';
import { IFilterProfile, ITankProfile } from '../interfaces/tankProfile';
import { IReportScope } from '../interfaces/reportScope';

@Injectable({
  providedIn: 'root',
})
export class ReportSettingsService {

  getTankCountByQuery(tankProfile: ITankProfile, filterProfile: IFilterProfile): Observable<number> {
    const filterModel = filterProfile?.tankFilterJson ? JSON.parse(filterProfile.tankFilterJson) : null;
    return this.restClient.postForResponse<number>(
      `${this.apiUrl}/tank-count-by-query/`,
      { tankProfile, filterModel },
    );
  }

  private readonly apiUrl: string = 'ReportSettings';

  constructor(private restClient: DSRESTClient) {}

  saveReportSettings(
    reportSettings: ReportSettingsSaveDto,
  ): Observable<number> {
    return this.restClient.postForResponse<number>(
      `${this.apiUrl}`,
      reportSettings,
    );
  }

  saveReportSettingsAndGenerate(
    reportSettings: ReportSettingsSaveDto,
  ): Observable<GenerateReportResult> {
    return this.restClient.postForResponse<GenerateReportResult>(
      `${this.apiUrl}/generate`,
      reportSettings,
    );
  }

  /** Status of the batch Generate Now started, or of the report's current batch when there is none. */
  getStatus(reportId: number, batchId?: string | null): Observable<number> {
    const query = batchId ? `?batchId=${encodeURIComponent(batchId)}` : '';
    return this.restClient.getAsModel<number>(
      `${this.apiUrl}/status/${reportId}${query}`,
    );
  }

  getReportTypes(): Observable<Dropdown<number>[]> {
    return this.restClient.getAsModel<Dropdown<number>[]>(
      `${this.apiUrl}/report-types`,
    );
  }

  getTimezones(): Observable<DropdownWithProperties<number, { adjustment: number }>[]> {
    return this.restClient.getAsModel<DropdownWithProperties<number, { adjustment: number }>[]>(
      `${this.apiUrl}/timezones`,
    );
  }

  getDateRangeOptions(id: number | null): Observable<Dropdown<number>[]> {
    const newLocal = id === null;
    const path = newLocal
      ? `${this.apiUrl}/date-range-options`
      : `${this.apiUrl}/date-range-options/${id}`;
    return this.restClient.getAsModel<Dropdown<number>[]>(path);
  }

  loadTankReportSettings(id: number): Observable<ReportSettingsSaveDto> {
    return this.restClient.getAsModel<ReportSettingsSaveDto>(
      `${this.apiUrl}/reports/${id}`,
    );
  }

  /**
   * Owning organization and CSO-branch answer for one report, for ReportNavigationGuard. Kept off
   * loadTankReportSettings so the guard does not pull the whole report graph - recipients, their
   * emails, their users and those users' organizations - on every navigation.
   */
  getReportScope(id: number): Observable<IReportScope> {
    return this.restClient.getAsModel<IReportScope>(
      `${this.apiUrl}/reports/${id}/scope`,
    );
  }

  getReportNames(reportId: number | null = null): Observable<string[]> {
    let reportNamesPath = `${this.apiUrl}/report-names`;
    if (reportId) {
      reportNamesPath = reportNamesPath.concat(`/${reportId}`);
    }
    return this.restClient.getAsModel<string[]>(reportNamesPath);
  }

  getUnitOfMeasures(): Observable<Dropdown<number>[]> {
    return this.restClient.getAsModel<Dropdown<number>[]>(
      `${this.apiUrl}/unit-of-measures`);
  }

  getNextRunTime(request: Criteria): Observable<NextDateResult> {
    let httpParams = new HttpParams();
    return this.restClient.postForResponse<NextDateResult>(
      `${this.apiUrl}/generate-next-run-time?` + httpParams.toString(),
      request,
    );
  }

  getTankProfiles(reportId: number | null, mode : string | null): Observable<ITankProfile[]> {
    if (reportId) {
      return this.restClient.getAsModel<ITankProfile[]>(
        `${this.apiUrl}/get-tank-profiles/${reportId}/${mode}`
      );
    }
    else
    {
      return this.restClient.getAsModel<ITankProfile[]>(
        `${this.apiUrl}/get-tank-profiles`
      );
    }
  }

  getFilterProfiles(reportId: number | null, mode: string | null): Observable<any> {
    if (reportId) {
      return this.restClient.getAsModel<IFilterProfile[]>(
        `${this.apiUrl}/get-filter-profiles/${reportId}/${mode}`
      );
    }
    else
    {
      return this.restClient.getAsModel<IFilterProfile[]>(
        `${this.apiUrl}/get-filter-profiles`
      );
    }
  }

  getReportStartDate(timezoneId: number | null): Observable<NextDateResult> {
    let httpParams = new HttpParams();
    return this.restClient.postForResponse<NextDateResult>(
      `${this.apiUrl}/get-report-start-date?`+ httpParams.toString(),
      timezoneId,
    );
  }


  getUsers(organizationId: number) : Observable<ReportUserDto[]> {
    return this.restClient.getAsModel<ReportUserDto[]>(
      `${this.apiUrl}/get-users/${organizationId}`);
  }

  SaveTankProfileForLegacy(
    reportId: number,
  ): Observable<any> {
    let httpParams = new HttpParams();
    return this.restClient.postForResponse<number>(
      `${this.apiUrl}/SaveTankProfileForLegacy?`+ httpParams.toString(),
      reportId,
    );
  }


  // getTimesNoEarlier(): Observable<Dropdown<number>[]> {
  //   return this.http.get<Dropdown<number>[]>(
  //     `${this.apiUrl}/GetTimesNoEarlier`
  //   );
  // }

  // getDateRange(request: GetDateRangeRequestDto): Observable<GetDateRangeResponseDto> {
  //   return this.http.post<GetDateRangeResponseDto>(`${this.apiUrl}/GetDateRange`, request);
  // }
}
